import { z } from 'zod';

import { CODEV_NATIVE_ANALYSES, CodevSettingValueSchema } from './codevCatalog.js';
import { CodevBatchResponseSchema } from './codevBatch.js';
import { WARNING_LEVELS } from './vocabulary.js';
export { CODEV_NATIVE_ANALYSES, type CodevNativeAnalysis } from './codevCatalog.js';

const NativeCellSchema = z.strictObject({
  /** Exact trimmed token; blank stays blank, .999 stays .999. */
  text: z.string(),
  value: z.number().nullable(),
});

/** Native plot style of a scatter series: CODE V's PlotLine/PlotPoints flags, nothing interpolated. */
const PlotModeSchema = z.enum(['markers', 'lines', 'lines+markers']);
const HeatmapSamplesSchema = z.strictObject({ first: z.number(), step: z.number() });
/** Presentation an analysis declares from native facts so the shared renderer needs no per-analysis branch. */
const scatterPresentation = {
  /** Group rows into separate traces by the exact native text of this column, in first-seen row order
   * (e.g. one trace per field or wavelength of a spot diagram). */
  series_column: z.number().int().nonnegative().optional(),
  /** Both axes are the same native length unit (spot diagrams, footprints): the renderer locks their scales. */
  equal_axes: z.boolean().optional(),
  /** Style for every trace, or per series (keyed by the series column's native cell text). */
  mode: PlotModeSchema.optional(),
  series_modes: z.record(z.string(), PlotModeSchema).optional(),
  /** Heading over the plot when it differs from the table title: the analysis drops parts of its native
   * title that the plot's axes already show (units, field type, the plotted quantity). */
  title: z.string().optional(),
};

export const CodevNativeTableSchema = z.strictObject({
  title: z.string(),
  columns: z.array(z.string()).min(1),
  rows: z.array(z.array(NativeCellSchema)),
  plot: z.union([
    z.strictObject({
      kind: z.literal('bar'),
      /** Exact native text labels, one per row; repeated or blank labels stay distinct bars. */
      category_column: z.number().int().nonnegative(),
      /** One series per column, grouped side by side: never stacked, summed or normalized. */
      value_columns: z.array(z.number().int().nonnegative()).min(1),
      x_label: z.string(), y_label: z.string(),
      title: z.string().optional(),
    }),
    // Array order, displayed as 1-based sample indices unless the analysis declares
    // native sample coordinates. No interpolation is inferred from a native grid.
    z.strictObject({
      kind: z.literal('heatmap'),
      x_label: z.string(), y_label: z.string(), z_label: z.string(),
      /** Explicit legend spacing declared by the analysis; does not change cells or color bounds. */
      z_tick_increment: z.number().positive().optional(),
      /** Column coordinates: column k (0-based, array order) is at first + k × step. Declared only from a
       * native grid centre and increment; cells and their order are unchanged. */
      x_samples: HeatmapSamplesSchema.optional(),
      /** Row coordinates, as `x_samples`; a negative step puts row 1 at the top of a normal axis. */
      y_samples: HeatmapSamplesSchema.optional(),
    }),
    z.strictObject({
      kind: z.literal('scatter_map'),
      x_column: z.number().int().nonnegative(),
      y_column: z.number().int().nonnegative(),
      value_column: z.number().int().nonnegative(),
      x_label: z.string(), y_label: z.string(), value_label: z.string(),
    }),

    z.strictObject({
      x_column: z.number().int().nonnegative(),
      y_columns: z.array(z.number().int().nonnegative()),
      x_label: z.string(),
      y_label: z.string(),
      /** Fixed bounds of CODE V's own plot of this quantity, only where that native axis was observed; otherwise
       * the axis fits the data. Length pinned for the Python validator as on `x_range`. */
      y_range: z.tuple([z.number(), z.number()]).meta({ minItems: 2, maxItems: 2 }).optional(),
      ...scatterPresentation,
    }),
    z.strictObject({
      y_column: z.number().int().nonnegative(),
      x_columns: z.array(z.number().int().nonnegative()),
      x_label: z.string(),
      /** Bounds from explicit native FIE/RAD plot scales, only produced on this form. zod emits a tuple as unbounded
       * `prefixItems`, so the length is pinned explicitly for the Python validator. */
      x_range: z.tuple([z.number(), z.number()]).meta({ minItems: 2, maxItems: 2 }).optional(),
      x_tick_increment: z.number().positive().optional(),
      y_label: z.string(),
      ...scatterPresentation,
    }),
  ]).nullable(),
});
export type CodevNativePlotMode = z.infer<typeof PlotModeSchema>;

const InputLoadSchema = z.strictObject({
  /** UTF-8 input bytes and native IN output; loaded/rejected is explicit on the envelope. */
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  load_output: z.string(),
});

const NativeCommandSchema = z.strictObject({ command: z.string(), output: z.string() });

/** Raw worker transport, shared by successful operations and error evidence. */
const workerFields = {
  mode: z.enum(['command', 'python']),
  input_seq: InputLoadSchema.nullable(),
  rejected_input_seq: InputLoadSchema.nullable(),
  commands: z.array(NativeCommandSchema).nullable(),
  output: z.string().nullable(),
  result: z.record(z.string(), z.unknown()).nullable(),
  stdout: z.string().nullable(),
  stderr: z.string().nullable(),
  modified_seq_content: z.string().nullable(),
};
export const CodevWorkerResponseSchema = z.union([
  z.strictObject({ ...workerFields, mode: z.literal('python'), result: z.record(z.string(), z.unknown()), success: z.literal(true), error: z.null() }),
  z.strictObject({ ...workerFields, mode: z.literal('command'), success: z.literal(true), error: z.null() }),
  z.strictObject({ ...workerFields, success: z.literal(false), error: z.string().min(1) }),
]);

export const CodevWorkerFailureSourceSchema = z.strictObject({
  worker_response: CodevBatchResponseSchema,
  batches: z.array(CodevBatchResponseSchema).optional(),
});

const NativeContextSchema = z.strictObject({
  input_seq: InputLoadSchema,
  zoom_count: z.number().int().positive(),
  wavelength_count: z.number().int().positive(),
  scalars: z.array(z.strictObject({ expression: z.string(), value: z.string(), output: z.string() })),
});

export const CodevNativeSourceSchema = z.strictObject({
  input_seq: InputLoadSchema,
  native_version: z.string(),
  dimension: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  max_text_buffer_size: z.number().int().positive(),
  /** Raw read-only native properties used to check complete surface/zoom listings. */
  lens_context: z.strictObject({
    surface_count: z.number().int().positive(), stop_surface: z.number().int().nonnegative(),
    zoom_count: z.number().int().positive(), wavelength_count: z.number().int().positive(),
    field_count: z.number().int().positive().optional(),
    scalars: z.array(z.strictObject({ expression: z.string(), value: z.string(), output: z.string() })),
  }).optional(),
  commands: z.array(NativeCommandSchema),
  execution_error: z.string().nullable(),
  /** Unmodified worker batches, each explicitly loading the same submitted SEQ. */
  batches: z.array(CodevBatchResponseSchema).min(1),
  /** Service-selected native scalar context, retaining its input identity. */
  context: NativeContextSchema.optional(),
});

/** Bump for incompatible meaning or required-shape changes. Additive optional presentation fields retain
 * existing report meaning, but strict consumers must be deployed before producers emit new fields. */
export const CODEV_NATIVE_REPORT_VERSION = 'codev-native/4';

/** Historical conversion evidence attached by the authenticated application, not the native engine. */
export const CodevImportProvenanceSchema = z.strictObject({
  system_id: z.uuid(),
  system_version: z.number().int().positive(),
  import_warnings: z.strictObject({
    normalization: z.array(z.object({ code: z.string(), message: z.string() }).catchall(z.unknown())),
    solve_conversions: z.array(z.object({ code: z.string(), message: z.string() }).catchall(z.unknown())),
  }),
});
export type CodevImportProvenance = z.infer<typeof CodevImportProvenanceSchema>;

/** One warning the export to CODE V raised, with the level the converter assigned its code. */
export const CodevConversionNoticeSchema = z.strictObject({ code: z.string(), level: z.enum(WARNING_LEVELS), message: z.string() });
export type CodevConversionNotice = z.infer<typeof CodevConversionNoticeSchema>;

const fields = {
  schema_version: z.literal(CODEV_NATIVE_REPORT_VERSION),
  analysis: z.enum(CODEV_NATIVE_ANALYSES),
  settings: z.record(z.string(), CodevSettingValueSchema),
  source: CodevNativeSourceSchema.nullable(),
  /** CODE V's own warnings for this analysis, each once. A report persisted before the service that writes
   * native-only lists was deployed opens with the export's conversion messages, each once and in the order of
   * its `conversion_notices`; one persisted before `conversion_notices` existed has no notices and is shown whole. */
  warnings: z.array(z.string()).describe("CODE V's own warnings for this analysis. A report persisted before the service that writes native-only lists was deployed also opens with the export's conversion messages, in the order of its conversion_notices; one persisted before conversion_notices existed has no notices and is shown whole."),
  /** The export's conversion warnings with their level, attached by the application that exported the lens
   * (never by the native service, which reports only what CODE V said); absent from reports persisted before
   * 2026-10-30. */
  conversion_notices: z.array(CodevConversionNoticeSchema).optional(),
  import_provenance: CodevImportProvenanceSchema.optional(),
};

export const CodevNativeReportSchema = z.union([
  z.strictObject({ ...fields, status: z.literal('ok'), success: z.literal(true), message: z.null(), tables: z.array(CodevNativeTableSchema).min(1), charts: z.array(z.string()).max(0) }),
  z.strictObject({ ...fields, source: z.union([CodevNativeSourceSchema, CodevWorkerFailureSourceSchema]).nullable(), status: z.enum(['unavailable', 'error']), success: z.literal(false), message: z.string(), tables: z.array(CodevNativeTableSchema).max(0), charts: z.array(z.string()).max(0) }),
]);
export type CodevNativeReport = z.infer<typeof CodevNativeReportSchema>;
export type CodevNativeTable = z.infer<typeof CodevNativeTableSchema>;

/** Report labels come directly from CODE V; this only decodes its unit enum. */
export const CODEV_DIMENSION_LABELS = { 0: 'inches', 1: 'centimeters', 2: 'millimeters' } as const;
